import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, Check, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const bW = image.width * cos + image.height * sin;
  const bH = image.width * sin + image.height * cos;

  canvas.width = bW;
  canvas.height = bH;
  ctx.translate(bW / 2, bH / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(data, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
};

const ImageCropModal = ({ src, onComplete, onClose }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setUploading(true);

    try {
      const blob = await getCroppedImg(src, croppedAreaPixels, rotation);
      if (!blob) return;

      const formData = new FormData();
      formData.append('file', blob, 'cropped-image.jpg');

      const token = localStorage.getItem('token');
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

      const res = await fetch(`${API}/dropbox/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (data.url) {
        onComplete(data.url);
      }
    } catch (err) {
      console.error('Crop upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[9999] flex flex-col" data-testid="crop-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-stone-900 text-white">
        <span className="font-medium text-sm tracking-wide uppercase">Crop & Edit Image</span>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50"
            data-testid="crop-save-btn"
          >
            <Check size={14} /> {uploading ? 'Saving...' : 'Apply'}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-stone-700 rounded"
            data-testid="crop-close-btn"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Crop area */}
      <div className="flex-1 relative">
        <Cropper
          image={src}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          showGrid={true}
        />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-8 px-6 py-4 bg-stone-900 text-white">
        <div className="flex items-center gap-3">
          <ZoomOut size={16} className="text-stone-400" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-32 accent-blue-500"
          />
          <ZoomIn size={16} className="text-stone-400" />
          <span className="text-xs text-stone-400 w-12">{Math.round(zoom * 100)}%</span>
        </div>

        <div className="w-px h-6 bg-stone-700" />

        <button
          onClick={() => setRotation((r) => (r + 90) % 360)}
          className="flex items-center gap-2 px-3 py-1.5 hover:bg-stone-700 rounded text-sm"
        >
          <RotateCw size={14} /> Rotate
        </button>

        <span className="text-xs text-stone-400">{rotation}°</span>
      </div>
    </div>
  );
};

export default ImageCropModal;
